import React, { useEffect, useState } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";

// Material Kit 2 PRO React components
import MKPagination from "components/MKPagination";

// Material Kit 2 PRO React examples
import DefaultNavbar from "hanyeleo/Navbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// History page sections
import EventList from "pages/Home/sections/EventList";

// Routes
import footerRoutes from "footer.routes";
import Axios from "utils/Axios";
import getNavRoutes from "../../navroutes";

// Images
import product5 from "assets/images/HanyeIcon.png";
import { useSelector } from 'react-redux';

const itemsPerPage = 10;

function History() {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState("");
  const selectedCity = useSelector((state) => state.city);
  const token = localStorage.getItem("token");
  const [routes, setNavRoutes] = useState([]);

  // Fetch and filter events based on page, selectedMonth, and selectedCity
  const fetchEvents = async (page = 1, city = "") => {
    const cityParam = city ? `&city=${city}` : "";
    const monthParam = selectedMonth ? `&month=${selectedMonth}` : "";
    try {
      const response = await Axios.get(`?type=event&event_time=past&page=${page}${cityParam}${monthParam}`);
      const allData = response.data.data;
      const pagination = response.data.pagination;
      
      setData(allData);
      setTotalPages(pagination.totalPages);
    } catch (error) {
      console.error("Failed to fetch events data", error);
    }
  };

  useEffect(() => {
    setNavRoutes(getNavRoutes(token ? true : false));
  }, []);

  useEffect(() => {
    setPage(1); // Reset to page 1 when filters change
    fetchEvents(1, selectedCity.id);
  }, [selectedMonth, selectedCity]);

  useEffect(() => {
    fetchEvents(page, selectedCity.id);
  }, [page]);

  // Handle page change for pagination
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Handle month filter change
  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
    setPage(1); // Reset to first page on filter change
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50 to-gray-100">
      <DefaultNavbar routes={routes} relative center />
      
      {/* Enhanced Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute top-40 right-20 w-64 h-64 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-700"></div>
          <div className="absolute bottom-20 left-1/2 w-64 h-64 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
        </div>

        <Container className="relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between py-16 md:py-20 md:space-x-12 px-6">
            <div className="w-full md:w-1/3 flex items-center justify-center mb-8 md:mb-0">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 rounded-full blur-2xl opacity-30 animate-pulse"></div>
                <img 
                  src={product5} 
                  alt="HanyeLeo Logo" 
                  className="relative z-10 drop-shadow-2xl transform hover:scale-105 transition-transform duration-500 max-w-[200px] md:max-w-full" 
                />
              </div>
            </div>

            <div className="w-full md:w-2/3 text-center md:text-left">
              <div className="inline-block mb-4 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-semibold">
                Relive the Memories
              </div>
              
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
                Past Events on{" "}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-300 via-pink-300 to-purple-300">
                  HanyeLeo
                </span>
              </h1>
              
              <p className="text-gray-200 text-lg md:text-xl mb-6 leading-relaxed">
                Missed an Event? Check out what you missed by viewing the image galleries of our previous events.
              </p>

              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
                  <span className="text-white font-medium">Epic Moments</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
                  <span className="text-white font-medium">Photo Galleries</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
                  <span className="text-white font-medium">Unforgettable Nights</span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Enhanced Title Section */}
      <Container>
        <div className="text-center my-16 md:my-20">
          <div className="inline-block mb-6">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl px-8 py-3 shadow-lg">
              <p className="text-2xl md:text-5xl font-bold text-white">Event History</p>
            </div>
          </div>
          
          <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-xl p-8 border-2 border-purple-100">
            <div className="flex items-center justify-center gap-3 mb-4">
              <p className="text-lg md:text-xl font-semibold text-gray-800">
                Explore Past Events
              </p>
            </div>
            <p className="text-gray-600 text-base md:text-lg">
              Click on the events below to see image galleries and relive the best moments from previous events
            </p>
          </div>
        </div>
      </Container>

      {/* Event List */}
      <EventList events={data} selectedLocation={selectedCity} />

      {/* Enhanced Pagination */}
      <Container sx={{ mt: 5, mb: 8 }}>
        <div className="bg-white rounded-2xl shadow-lg p-6 flex justify-center">
          <MKPagination count={totalPages} page={page} onChange={handlePageChange}>
            <MKPagination 
              item 
              disabled={page === 1} 
              onClick={() => handlePageChange(null, page - 1)}
              className="hover:bg-purple-100 transition-colors duration-300"
            >
              <Icon>keyboard_arrow_left</Icon>
            </MKPagination>
            {[...Array(totalPages).keys()].map((pageNumber) => (
              <MKPagination
                item
                key={pageNumber + 1}
                onClick={() => handlePageChange(null, pageNumber + 1)}
                active={page === pageNumber + 1}
                className={page === pageNumber + 1 ? "bg-gradient-to-r from-purple-600 to-pink-600" : "hover:bg-purple-50"}
              >
                {pageNumber + 1}
              </MKPagination>
            ))}
            <MKPagination 
              item 
              disabled={page === totalPages} 
              onClick={() => handlePageChange(null, page + 1)}
              className="hover:bg-purple-100 transition-colors duration-300"
            >
              <Icon>keyboard_arrow_right</Icon>
            </MKPagination>
          </MKPagination>
        </div>
      </Container>

      <DefaultFooter content={footerRoutes} />
    </div>
  );
}

export default History;